import { Check, Palette as PaletteIcon } from 'lucide-react';
import { type CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { applyTheme } from '../../hooks/useTheme.js';
import {
  FONTS, applyFont, ensureFontLoaded, fontStacks, loadFontTheme, saveFontTheme,
} from '../../lib/fontTheme.js';
import { PALETTES, applyPalette, loadPalette, savePalette, swatchAccent } from '../../lib/palette.js';

function isDarkNow(): boolean {
  return typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';
}

/**
 * Appearance picker — a chrome icon button that opens the color-theme swatch
 * grid plus the app-wide font list. Both live-apply on select
 * (lib/palette.applyPalette / lib/fontTheme.applyFont) and persist to
 * localStorage, so the choice is per user + browser like light/dark.
 */
export function PaletteSwitcher(): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<string>(() => loadPalette());
  const [font, setFont] = useState<string>(() => loadFontTheme());
  const [hoverName, setHoverName] = useState<string | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  // Close on outside-click / Escape.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent): void {
      const target = e.target as Node;
      if (popRef.current?.contains(target) || btnRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // The font rows preview themselves, so opening the picker is what pays for
  // the families that are not in the entry bundle. Once each, never on boot.
  useEffect(() => {
    if (open) for (const f of FONTS) ensureFontLoaded(f.id);
  }, [open]);

  const choose = useCallback((id: string): void => {
    savePalette(id);
    const p = PALETTES.find((x) => x.id === id);
    let dark = isDarkNow();
    // Dark-only themes (Black / Near black) flip the app to dark on select and
    // sync the light/dark toggle (same-tab StorageEvent → useTheme re-reads).
    if (p?.forceDark && !dark) {
      dark = true;
      try { window.localStorage.setItem('erp-theme', 'dark'); } catch { /* ignore */ }
      applyTheme('dark');
      try { window.dispatchEvent(new StorageEvent('storage', { key: 'erp-theme', newValue: 'dark' })); } catch { /* ignore */ }
    }
    applyPalette(id, dark);
    setCurrent(id);
  }, []);

  const chooseFont = useCallback((id: string): void => {
    ensureFontLoaded(id);
    saveFontTheme(id);
    applyFont(id);
    setFont(id);
  }, []);

  const activeName = PALETTES.find((p) => p.id === current)?.name ?? PALETTES[0]!.name;
  const label = t('actions.appearance', { defaultValue: 'Appearance' });
  const sample = t('font.sample', { defaultValue: 'Ag 123' });

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="icon-btn"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={label}
        onClick={() => setOpen((o) => !o)}
      >
        <PaletteIcon aria-hidden="true" />
        <span className="pal-dot" aria-hidden="true" />
      </button>

      {open && (
        <div ref={popRef} className="pal-pop" role="dialog" aria-label={label}>
          <div className="ph">{t('actions.colorTheme')}</div>
          <div className="pal-grid" onMouseLeave={() => setHoverName(null)}>
            {PALETTES.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`pal-sw${p.id === current ? ' on' : ''}`}
                title={p.name}
                aria-label={p.name}
                aria-pressed={p.id === current}
                style={{ background: p.swatch ?? p.primary, '--sw-accent': swatchAccent(p) } as CSSProperties}
                onMouseEnter={() => setHoverName(p.name)}
                onClick={() => choose(p.id)}
              />
            ))}
          </div>
          <div className="pal-name">{hoverName ?? activeName}</div>

          <div className="pal-sec">
            <div className="ph">{t('font.title')}</div>
            <div className="fnt-list">
              {FONTS.map((f) => {
                const name = f.labelKey ? t(f.labelKey) : f.name;
                return (
                  <button
                    key={f.id}
                    type="button"
                    className={`fnt-row${f.id === font ? ' on' : ''}`}
                    aria-pressed={f.id === font}
                    style={{ fontFamily: fontStacks(f).sans }}
                    onMouseEnter={() => ensureFontLoaded(f.id)}
                    onClick={() => chooseFont(f.id)}
                  >
                    <span className="fnt-name">{name}</span>
                    <span className="fnt-sample" aria-hidden="true">{sample}</span>
                    {f.id === font ? <Check className="fnt-check" aria-hidden="true" /> : null}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
